<template>
  <div
    class="divide-y divide-solid divide-bg overflow-hidden rounded-2xl border border-solid border-line bg-white shadow-[0_34px_64px_-30px_rgba(22,34,30,0.45)]"
    data-testid="YourAlerts"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-[18px] py-4">
      <div class="flex items-center gap-[9px]">
        <svg
          class="h-[17px] w-[17px] text-accent"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 01-3.4 0" />
        </svg>
        <span class="font-display text-base font-extrabold tracking-tight text-ink">
          {{ $t('pages.journalists.sections.alerts.title') }}
        </span>
      </div>
      <span class="rounded-full bg-accent-soft px-3 py-[5px] font-display text-xs font-bold text-accent">
        {{ $t('pages.journalists.sections.alerts.daily') }}
      </span>
    </div>

    <!-- Loading skeleton -->
    <template v-if="isLoading">
      <div
        v-for="n in 3"
        :key="`skeleton-${n}`"
        class="flex items-center gap-3 px-[18px] py-[13px]"
      >
        <span class="h-[46px] w-[46px] shrink-0 animate-pulse rounded-[9px] bg-bg" />
        <div class="flex-1 space-y-2">
          <span class="block h-[10px] w-1/3 animate-pulse rounded bg-bg" />
          <span class="block h-[12px] w-3/4 animate-pulse rounded bg-bg" />
        </div>
      </div>
    </template>

    <!-- Alerts -->
    <template v-else-if="alerts.length">
      <router-link
        v-for="story in alerts"
        :key="story.id"
        :to="storyRoute(story)"
        class="group flex items-center gap-3 px-[18px] py-[13px] transition-colors hover:bg-row-hover"
        data-testid="YourAlerts__row"
      >
        <span
          class="h-[46px] w-[46px] shrink-0 rounded-[9px] bg-accent-soft bg-cover bg-center"
          :style="coverStyle(story)"
        />
        <div class="min-w-0 flex-1">
          <div class="truncate font-display text-xs font-semibold text-muted-faint">
            {{ story.newsroom.name }} · {{ timeFromNow(story.publishedAt) }}
          </div>
          <div class="truncate font-display text-sm font-bold leading-tight tracking-tight text-ink transition-colors group-hover:text-accent">
            {{ story.headline }}
          </div>
        </div>
        <span class="h-2 w-2 shrink-0 rounded-full bg-accent" />
      </router-link>
    </template>

    <!-- Empty / error fallback -->
    <div
      v-else
      class="px-[18px] py-8 text-center font-display text-base text-muted-soft"
      data-testid="YourAlerts__empty"
    >
      {{ $t('components.story_search.no_stories_match_your_search_criteria') }}
    </div>
  </div>
</template>

<script>
import { Story } from '@/shared/js/models/Story'
import { StoryApiService } from '@/shared/js/services/api/StoryApiService'

import TimeFromNowMixin from '@/shared/js/mixins/TimeFromNowMixin'

/**
 * Live "Your alerts" widget — shows the 3 most-recent published stories in the
 * viewer's current platform language, each linking to its full story.
 * @module YourAlerts
 */
export default {
  name: 'YourAlerts',

  mixins: [TimeFromNowMixin],

  data () {
    return {
      alerts: [],
      isLoading: true
    }
  },

  mounted () {
    this.fetchAlerts()
  },

  methods: {
    fetchAlerts () {
      this.isLoading = true
      // Public page, no active brand — mirror the public feed: sort by recency,
      // filter to the viewer's platform language, cap at 3.
      return StoryApiService.fetchStories({
        params: {
          include: 'newsroom',
          sort: 'newest',
          language: this.$i18n.locale,
          limit: 3
        }
      })
        .then(response => {
          this.alerts = (response.data.data || []).map(raw => new Story(raw))
        })
        .catch(() => {
          // Marketing surface — fail quiet, the empty state covers it.
          this.alerts = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    storyRoute (story) {
      return {
        name: 'story',
        params: {
          newsroomSlug: story.newsroomSlug || story.newsroom.slug,
          storySlug: story.slug,
          newsroomId: story.newsroom.id,
          storyId: story.id
        }
      }
    },

    coverStyle (story) {
      const url = story.cover.cropped || story.newsroom.logo.cropped
      return url ? { backgroundImage: `url('${url}')` } : {}
    }
  }
}
</script>
